import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { Grid, Tab, Divider, Message, Container } from "semantic-ui-react";
import { intersection } from "lodash";
import axios from "axios";

import { LinkObject, CaseType, CaseStatus } from "@teamprevent/common/model";
import { MedicalType, medicalTypes, MedicalTypeId } from "@teamprevent/common/healthSurveillance/models";
import { TaskTypes } from "@teamprevent/common/task/taskActions/models";
import { isSystemAdminUser } from "@teamprevent/common/auth/roles";
import { MessagingModalButton } from "@teamprevent/common/messaging/components/MessagingModalButton";

import { useCustomMedicalTypes } from "../../../../customMedicalTypes/hooks/useCustomMedicalTypes";

import { LinkedCaseButton } from "../LinkedCaseButton";
import { OhCaseTasks } from "../../../../task/OhCaseTasks";
import { CreateLinkedImmunisationCaseButton } from "../../../../ppq/components/CreateLinkedImmunisationCaseButton";
import { CreateLinkedPhysiotherapyCaseButton } from "../../../../referrals/components/CreateLinkedPhysiotherapyCaseButton";
import { CreateLinkedMHPCaseButton } from "../../../../referrals/components/CreateLinkedMHPCaseButton";
import { AuthorizeProducts } from "../../../../auth/AuthorizeProducts";
import { OhCase, OhCaseDetailModel } from "../../model";
import { ApplicationState } from "../../../../applicationState";
import { CUSTOMER_ROLES } from "../../../../auth/model";
import { loadEmployeeRecordDetail } from "../../../employeeRecord/actions";
import { LineManagersApi } from "../../../../lineManagers/LineManagersApi";
import { HrRepresentativesApi } from "../../../../hrRepresentatives/HrRepresentativesApi";

import { loadOhCaseDetail } from "../../actions";

import { Customer, CustomerDetailModel } from "../../../customer/model";

import { AssociatedManagersEditor, AssociateOption } from "./AssociatedManagersEditor";
import { CloseCaseModal } from "./CloseCaseModal";
import { CloseHSFFWCaseButton } from "./CloseHSFFWCaseButton";
import { ReopenHealthSurveillanceCaseButton } from "./healthSurveillanceCase/ReopenHealthSurveillanceCaseButton";

import "../../styles/ohCase.scss";
import { ViewCaseFormButtons } from "./ViewCaseFormButtons";
import { OhCaseEmployeeDetails } from "./OhCaseEmployeeDetails";
import { CoreDetailsInfo } from "./CoreDetailsInfo";
import { CoreDetailsTaskActions } from "./CoreDetailsTaskActions";

interface Props {
    ohCase: OhCaseDetailModel | OhCase;
    customerId: string;
    customer: CustomerDetailModel | Customer;
    updateAssociatedManagers: (customerId: string, ohCaseId: string, managers: LinkObject[]) => Promise<void>;
    updateAssociatedHrUsers: (customerId: string, ohCaseId: string, managers: LinkObject[]) => Promise<void>;
    refreshCase: (ohCaseId: string, customerId: string) => void;
}

const lineManagersApi = new LineManagersApi(axios);
const hrRepresentativesApi = new HrRepresentativesApi(axios);
export const CoreDetails: React.FC<Props> = (props) => {
    const dispatch = useDispatch();

    const [taskCategories, setTaskCategories] = React.useState<string[]>([]);
    const [taskTypeSearch, setTaskTypeSearch] = React.useState("");
    const [managerOptions, setManagerOptions] = React.useState<AssociateOption[]>([]);
    const [hrUserOptions, setHrUserOptions] = React.useState<AssociateOption[]>([]);
    const { customMedicalTypes, isLoaded } = useCustomMedicalTypes();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const { ohCase: { caseType }, customerId } = props;
    React.useEffect(() => {
        async function fetchManagers() {
            setIsLoading(true);
            if (caseType) {
                const [managers, hrUsers] = await Promise.all([
                    lineManagersApi.getAll(customerId, caseType === CaseType.PPQ ? CUSTOMER_ROLES.RECRUITMENT_MANAGER.name : null),
                    hrRepresentativesApi.getAll(customerId)
                ]);

                setManagerOptions(managers.map(m => ({ id: m.id, name: m.name, email: m.email })));
                setHrUserOptions(hrUsers.map(m => ({ id: m.id, name: m.name, email: m.email })));
                setIsLoading(false);
            }
        }
        fetchManagers();
    }, [caseType, customerId]);

    const ohCaseMedicalTypeIds: MedicalTypeId[] | undefined = props.ohCase?.medicalTypes;

    let mergedMedicals = medicalTypes;
    if (isLoaded) {
        mergedMedicals = mergedMedicals.concat(customMedicalTypes.map(m => m as MedicalType));
    }

    const ohCaseMedicalTypes: MedicalType[] = mergedMedicals.filter(mt => ohCaseMedicalTypeIds?.find(mdi => mdi === mt.id));
    const currentUser = useSelector((state: ApplicationState) => state.currentUser);
    const ohCaseId: string | undefined = useSelector((state: ApplicationState) => state.router.params?.ohCaseId);
    const ohCase: OhCase | undefined = useSelector((state: ApplicationState) => state.ohCases?.find(c => c.id === ohCaseId));

    React.useEffect(() => {
        if (!ohCase && props.customerId && ohCaseId) {
            dispatch(loadOhCaseDetail({ customerId: props.customerId, ohCaseId }));
        }
    }, [props.customerId, ohCaseId, ohCase, dispatch]);

    const employeeRecord = useSelector((state: ApplicationState) =>
        state.employeeRecordsDetail?.employeeRecords?.find(er => er.id === props.ohCase?.employeeRecordId));

    React.useEffect(() => {
        if (!employeeRecord && props.customerId && props.ohCase?.employeeRecordId) {
            dispatch(loadEmployeeRecordDetail({ customerId: props.customerId, employeeRecordId: props.ohCase?.employeeRecordId }));
        }
    }, [props.customerId, props.ohCase?.employeeRecordId, employeeRecord, dispatch]);

    const isNotLinkedToCaseOfType = (ohCaseModel: OhCase, caseOfType: CaseType) => {
        if (ohCaseModel?.linkedOhCases) {
            const index = ohCaseModel.linkedOhCases.findIndex(c => c.caseType === caseOfType);
            return index === -1;
        }
        return true;
    };

    const shouldRenderCreateLinkedImmunisationCaseButton = React.useCallback(() => {
        const isOfSuitableCaseType = [CaseType.PPQ, CaseType.BBV].indexOf(props.ohCase.caseType as CaseType) !== -1;
        const isNotLinkedToImmsCase = isNotLinkedToCaseOfType(props.ohCase, CaseType.IMMUNISATION);
        const isClearanceConfirmed = props.ohCase.tasks?.find(t => t.type === TaskTypes.ImmunisationClearanceTask);
        return isOfSuitableCaseType && isNotLinkedToImmsCase && !isClearanceConfirmed;
    }, [props.ohCase]);

    const shouldRenderCreateLinkedPhysiotherapyCaseButton = [CaseType.REFERRAL].indexOf(props.ohCase.caseType as CaseType) !== -1
        && isNotLinkedToCaseOfType(props.ohCase, CaseType.PHYSIOTHERAPY_REFERRAL);

    const shouldRenderCreateMHPCaseButton = [CaseType.REFERRAL].indexOf(props.ohCase.caseType as CaseType) !== -1
        && isNotLinkedToCaseOfType(props.ohCase, CaseType.MHP_TREATMENT);

    const shouldRenderCloseHSFFWCaseCloseDnaButton = props.ohCase.caseType === CaseType.HEALTH_SURVEILLANCE
        && props.ohCase.caseStatus === CaseStatus.Open;

    return (
        <>
            <Grid>
                <CoreDetailsInfo currentUser={currentUser}
                    customer={props.customer}
                    customerId={props.customerId}
                    refreshCase={props.refreshCase}
                    ohCase={props.ohCase} />
                <Grid.Row>
                    <Grid.Column width={16}><Divider /></Grid.Column>
                </Grid.Row>
                <OhCaseEmployeeDetails ohCase={props.ohCase} />
                <Grid.Row columns="2">
                    <Grid.Column>
                        <AssociatedManagersEditor label="Associated managers"
                            noDataMessage="There are no managers associated with this case"
                            managers={props.ohCase.associatedManagers}
                            onSave={(managers) => props.updateAssociatedManagers(props.customerId, props.ohCase.id, managers)}
                            associateOptions={managerOptions}
                            editable={!props.ohCase.totallyConfidential || isSystemAdminUser(currentUser)}
                            isLoading={isLoading}
                        />
                    </Grid.Column>
                    <Grid.Column>
                        <AssociatedManagersEditor label="Associated HR managers"
                            noDataMessage="There are no HR managers associated with this case"
                            managers={props.ohCase.associatedHrUsers}
                            onSave={(managers) => props.updateAssociatedHrUsers(props.customerId, props.ohCase.id, managers)}
                            associateOptions={hrUserOptions}
                            editable={!props.ohCase.totallyConfidential || isSystemAdminUser(currentUser)}
                            isLoading={isLoading}
                        />
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Message color={props.ohCase.criticalNotes ? "red" : undefined}>
                            <Message.Header>Critical Notes</Message.Header>
                            <p>
                                {props.ohCase.criticalNotes}
                            </p>
                        </Message>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Message>
                            <Message.Header>Customer Notes</Message.Header>
                            <p>{props.ohCase.customerNotes}</p>
                        </Message>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Container textAlign="right">
                            <ViewCaseFormButtons
                                ohCase={props.ohCase}
                                customerId={props.customerId}
                            />
                            {props.ohCase && props.ohCase.tasks &&
                                props.ohCase.tasks.filter(task => task.cancelled === false && task.completed === false).length > 0 &&
                                <CloseCaseModal ohCase={props.ohCase} customerId={props.customerId} />
                            }
                        </Container >
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            <Divider />
            <CoreDetailsTaskActions
                customerId={ props.customerId}
                ohCase={ props.ohCase }
                ohCaseMedicalTypes={ ohCaseMedicalTypes }
                setTaskCategories={ setTaskCategories }
                setTaskTypeSearch={ setTaskTypeSearch }
                taskCategories={ taskCategories }
            />
            {props.ohCase.tasks && <div className="tasks-tab-bar">
                <OhCaseTasks
                    tasks={props.ohCase.tasks
                        .filter(t => t.description.toUpperCase().indexOf(taskTypeSearch.toUpperCase()) !== -1)
                        .filter(t => taskCategories.length === 0 || intersection(taskCategories, t.taskCategories).length > 0)
                    }
                    customerId={props.customerId}
                />
            </div>}
            {props.ohCase.linkedOhCases && props.ohCase.linkedOhCases.length > 0 && <>
                <Divider />
                <Container textAlign="right">
                    {props.ohCase.linkedOhCases.map(linkedCase => (
                        <LinkedCaseButton
                            customerId={props.customerId}
                            employeeRecordId={props.ohCase.employeeRecordId}
                            linkedCase={linkedCase}
                            key={linkedCase.id}
                        />
                    ))}
                </Container>
            </>}
            {shouldRenderCreateLinkedImmunisationCaseButton() &&
                <AuthorizeProducts customer={props.customer} productTypes={CaseType.IMMUNISATION}>
                    <CreateLinkedImmunisationCaseButton />
                </AuthorizeProducts>
            }
            {shouldRenderCreateLinkedPhysiotherapyCaseButton &&
                <AuthorizeProducts customer={props.customer} productTypes={CaseType.PHYSIOTHERAPY_REFERRAL}>
                    <CreateLinkedPhysiotherapyCaseButton linkedOhCaseId={props.ohCase.id} />
                </AuthorizeProducts>
            }
            {shouldRenderCreateMHPCaseButton &&
                <AuthorizeProducts customer={props.customer} productTypes={CaseType.MHP_TREATMENT}>
                    <CreateLinkedMHPCaseButton linkedOhCaseId={props.ohCase.id} />
                </AuthorizeProducts>
            }
            <Grid.Row>
                <Grid.Column>
                    <div className={"conversation-margin"}>
                        <MessagingModalButton customerId={props.customerId} caseId={props.ohCase.id} buttonText={"Contact Manager / HR about this case"} />
                        {shouldRenderCloseHSFFWCaseCloseDnaButton &&
                            <CloseHSFFWCaseButton
                                customerId={props.customerId}
                                ohCaseId={props.ohCase.id}
                                complete={() => props.refreshCase(props.ohCase.id, props.customer.id)}
                            />
                        }
                    </div>
                </Grid.Column>
            </Grid.Row>
            <AuthorizeProducts customer={props.customer} productTypes={CaseType.HEALTH_SURVEILLANCE}>
                <ReopenHealthSurveillanceCaseButton ohCase={props.ohCase} />
            </AuthorizeProducts>
        </>
    );
};

const CoreDetailsWithActions: React.FunctionComponent<Props> = (props) => {
    return (
        <CoreDetails
            ohCase={props.ohCase}
            customerId={props.customerId}
            refreshCase={props.refreshCase}
            updateAssociatedHrUsers={props.updateAssociatedHrUsers}
            updateAssociatedManagers={props.updateAssociatedManagers}
            customer={props.customer}
        />
    );
};

export const coreDetailsTab = (props: Props) => ({
    path: "",
    menuItem: "Details",
    render: () => <Tab.Pane><CoreDetailsWithActions {...props} /></Tab.Pane>
});
